import type { DoorConfiguration } from '../types/doorConfiguration';

/** Head and jamb thickness used to inset the slab opening. Millimetres. */
export const FRAME_BORDER_MM = 60;
/** Optional cill band height. Millimetres. Layout-owned; not taken from profile artwork. */
export const CILL_HEIGHT_MM = 40;
/** Threshold band height. Millimetres. Layout-owned; not taken from profile artwork. */
export const THRESHOLD_HEIGHT_MM = 25;

/**
 * Axis-aligned rectangle.
 * Layout values are millimetres. Asset metadata may reuse this shape for source-SVG user units.
 */
export interface RectMm {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface DoorLayout {
  overallBounds: RectMm;
  frameBounds: RectMm;
  slabOpeningBounds: RectMm;
  /** Null when the cill is not selected. Full-width band at the overall bottom. */
  cillBounds: RectMm | null;
  /**
   * Null when the threshold is not selected.
   * Slab-width band between the jambs; sits on the cill, or on the overall bottom when there is no cill.
   */
  thresholdBounds: RectMm | null;
}

/** Top-left of the overall installation is (0, 0). All returned bounds are millimetres. */
export function computeDoorLayout(configuration: DoorConfiguration): DoorLayout {
  const width = configuration.dimensions.overallWidthMm;
  const height = configuration.dimensions.overallHeightMm;
  const { hasCill, hasThreshold } = configuration.ancillaries;

  const overallBounds: RectMm = { x: 0, y: 0, width, height };

  const cillHeight = hasCill ? CILL_HEIGHT_MM : 0;
  const thresholdHeight = hasThreshold ? THRESHOLD_HEIGHT_MM : 0;

  const cillBounds: RectMm | null = hasCill
    ? { x: 0, y: height - cillHeight, width, height: cillHeight }
    : null;

  const frameHeight = height - cillHeight;
  const frameBounds: RectMm = { x: 0, y: 0, width, height: frameHeight };

  const slabX = FRAME_BORDER_MM;
  const slabWidth = width - FRAME_BORDER_MM * 2;
  const slabBottom = frameHeight - thresholdHeight;

  const slabOpeningBounds: RectMm = {
    x: slabX,
    y: FRAME_BORDER_MM,
    width: slabWidth,
    height: slabBottom - FRAME_BORDER_MM,
  };

  const thresholdBounds: RectMm | null = hasThreshold
    ? { x: slabX, y: slabBottom, width: slabWidth, height: thresholdHeight }
    : null;

  return {
    overallBounds,
    frameBounds,
    slabOpeningBounds,
    cillBounds,
    thresholdBounds,
  };
}
